import { useState } from 'react';
import { API } from '@/utils/API';

interface UseRoleAssignmentActionsReturn {
  updateRole: (
    resourcePath: string,
    roleAssignmentId: string,
    newRoleKey: string
  ) => Promise<void>;
  deleteRole: (resourcePath: string, roleAssignmentId: string) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

export const useRoleAssignmentActions = (): UseRoleAssignmentActionsReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateRole = async (
    resourcePath: string,
    roleAssignmentId: string,
    newRoleKey: string
  ) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await API.authenticated.fetch(
        `${resourcePath}/role_assignments/${roleAssignmentId}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ role_key: newRoleKey }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update role');
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteRole = async (resourcePath: string, roleAssignmentId: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await API.authenticated.fetch(
        `${resourcePath}/role_assignments/${roleAssignmentId}`,
        {
          method: 'DELETE',
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete role');
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    updateRole,
    deleteRole,
    isLoading,
    error,
  };
};
